import type { KpiItem } from "../types";
import { cn } from "@/shared/lib/utils";
import { EvidenceBadge } from "../EvidenceBadge";
import { useCountUp } from "./useCountUp";

/**
 * Compact KPI strip — calmer than the project's ui/stat-card.tsx by design.
 * stat-card.tsx is decorative (animated gradients, scale-105, gradient text)
 * and conflicts with the concise-first goal of letting the answer + numerics
 * lead. We reuse the status-token vocabulary (success/warning/critical/info)
 * but render as a flat tiled row.
 */
export function KpiRow({ items }: { items: KpiItem[] }) {
  const count = items.length;
  return (
    <div
      className={cn(
        "grid gap-px bg-border",
        count >= 4
          ? "grid-cols-2 md:grid-cols-4"
          : count === 3
            ? "grid-cols-3"
            : count === 2
              ? "grid-cols-2"
              : "grid-cols-1",
      )}
    >
      {items.map((item, i) => (
        <KpiTile key={i} item={item} />
      ))}
    </div>
  );
}

// Value tint for the optional KpiItem.tone — the same status token vocabulary
// SectionTracker/SectionCallout use.
const TONE_TEXT: Record<NonNullable<KpiItem["tone"]>, string> = {
  critical: "text-status-critical",
  warning: "text-status-warning",
  success: "text-status-success",
};

// Arc stroke per tone for the mini gauge — same --status-* HSL tokens as
// TONE_TEXT; default = primary.
const TONE_STROKE: Record<NonNullable<KpiItem["tone"]>, string> = {
  critical: "hsl(var(--status-critical))",
  warning: "hsl(var(--status-warning))",
  success: "hsl(var(--status-success))",
};

function polarPoint(cx: number, cy: number, r: number, pct: number) {
  // 0% → 180° (left), 100% → 0° (right); upper semicircle.
  const angle = Math.PI * (1 - pct / 100);
  return { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) };
}

function semiArcPath(cx: number, cy: number, r: number, fromPct: number, toPct: number) {
  const a = polarPoint(cx, cy, r, fromPct);
  const b = polarPoint(cx, cy, r, toPct);
  return `M ${a.x} ${a.y} A ${r} ${r} 0 0 1 ${b.x} ${b.y}`;
}

// Small semicircular gauge rendered beside the KPI value when item.gauge is
// present. ~64px wide so stacked KPI rows stay compact. Hand-rolled SVG, not
// recharts: kpi_row is in the concise-first lead render, which must keep
// shipping zero chart code (R8 lock — only chart/stat_table/sparkline lazy-load
// recharts).
function MiniGauge({ gauge, tone }: { gauge: NonNullable<KpiItem["gauge"]>; tone?: KpiItem["tone"] }) {
  const cx = 100;
  const cy = 92;
  const r = 78;
  const value = Math.max(0, Math.min(100, gauge.value));
  const stroke = tone ? TONE_STROKE[tone] : "hsl(var(--primary))";
  const tick =
    gauge.target != null
      ? {
          outer: polarPoint(cx, cy, r + 10, gauge.target),
          inner: polarPoint(cx, cy, r - 10, gauge.target),
        }
      : null;
  return (
    <svg
      viewBox="0 0 200 104"
      className="w-16 shrink-0"
      role="img"
      // "reference", not "target": the tick is a query-computed reference
      // value (e.g. portfolio average); invented-target language is banned by
      // the dashboard spec's binding data rules.
      aria-label={
        gauge.target != null ? `${value}%, reference ${gauge.target}%` : `${value}%`
      }
    >
      <path
        d={semiArcPath(cx, cy, r, 0, 100)}
        fill="none"
        stroke="hsl(var(--muted) / 0.6)"
        strokeWidth={16}
        strokeLinecap="round"
      />
      <path
        d={semiArcPath(cx, cy, r, 0, value)}
        fill="none"
        stroke={stroke}
        strokeWidth={16}
        strokeLinecap="round"
      />
      {tick ? (
        <line
          x1={tick.inner.x}
          y1={tick.inner.y}
          x2={tick.outer.x}
          y2={tick.outer.y}
          stroke="hsl(var(--foreground))"
          strokeWidth={4}
        />
      ) : null}
    </svg>
  );
}

function KpiTile({ item }: { item: KpiItem }) {
  // Count-up runs on mount; KpiRow only mounts once the section commits, so the
  // animation never plays against a half-streamed number (B4 rule 7).
  const display = useCountUp(item.value);
  const valueClassName = cn(
    "text-3xl font-semibold leading-tight tabular-nums",
    item.tone ? TONE_TEXT[item.tone] : "text-foreground",
  );
  return (
    <div className="bg-card px-5 py-4 flex flex-col gap-1 relative">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {item.label}
        </span>
        {item.evidence_urls && item.evidence_urls.length > 0 ? (
          <EvidenceBadge urls={item.evidence_urls} caption={{ leaf: item.label }} size="sm" />
        ) : null}
      </div>
      {item.gauge ? (
        <div className="flex items-end gap-2.5">
          <MiniGauge gauge={item.gauge} tone={item.tone} />
          <span className={valueClassName}>{display}</span>
        </div>
      ) : (
        <span className={valueClassName}>{display}</span>
      )}
      {item.sublabel ? (
        <span className="text-xs text-muted-foreground">{item.sublabel}</span>
      ) : null}
    </div>
  );
}
